import React, { useState } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Flex, Divider, TextV2, FabButton, Box } from '@withjoy/joykit';
import { HeartStroke, HotelProduct, ZoomContract, ZoomExpand } from '@withjoy/joykit/icons';
import { JoyInteractableMap, MAP_CARDS_PORTAL_ID, MapPin, MapPinWithCard } from '@shared/components/JoyInteractableMap';

import { StyledTitle, mapSizeVariants } from './HotelMap.styles';
import { LeadDestination, ReservedRoomBlocks } from '../../widgets/Custom/Custom.types';
import { animationTransition } from '@shared/utils/animationTransition';

// @todo - https://withjoy.atlassian.net/browse/CORE-4529
import { HotelRating } from '@apps/admin/routes/Planning/routes/HotelSelection/routes/HotelSelectionList/components/HotelRating';

interface HotelMapProps {
  pageTitle?: string;
  destination: LeadDestination;
  reservedRoomBlocks: ReservedRoomBlocks;
  onSelectHotel: (id: string) => void;
}

const HotelMap: React.FC<HotelMapProps> = ({ pageTitle, destination, reservedRoomBlocks, onSelectHotel }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));

  const buttonSize = isFullScreen ? 48 : 40;
  const mapSize = isFullScreen ? mapSizeVariants.fullscreen : mapSizeVariants.normalscreen;
  const latlng = { lat: destination.lat ?? 0, lng: destination.long ?? 0 };

  const switchScreenMode = useEventCallback(() => {
    setActiveMarker(null);
    setIsFullScreen(fullscreen => !fullscreen);
  });

  return (
    <>
      <Flex width={'100%'}>
        <Flex {...mapSize} borderRadius={{ _: 2, sm4: 3 }} overflow="hidden">
          <JoyInteractableMap
            center={latlng}
            onClick={() => {
              if (activeMarker) {
                setActiveMarker(null);
              }
            }}
            autoCenterAndZoom
          >
            {isFullScreen && pageTitle && (
              <Box padding={6} width="100%" textAlign="center" position="absolute" backgroundColor="white">
                <StyledTitle typographyVariant="display4" color="mono12" fontSize={{ _: 40, sm2: 48 }} fontWeight={{ _: 52, sm2: 62.4 }}>
                  {pageTitle}
                </StyledTitle>
              </Box>
            )}
            <FabButton
              right={5}
              top={isFullScreen ? 124 : 5}
              padding={0}
              position="absolute"
              variant="ghostShadow"
              minWidth={pxToRem(buttonSize)}
              maxWidth={pxToRem(buttonSize)}
              width={pxToRem(buttonSize)}
              height={pxToRem(buttonSize)}
              onClick={switchScreenMode}
            >
              {isFullScreen ? <ZoomContract size="md" /> : <ZoomExpand size="sm" />}
            </FabButton>
            <MapPin
              position={{ lat: latlng.lat || 0, lng: latlng.lng || 0 }}
              showOnTop
              label={
                <Flex justifyContent="center" alignItems="center" columnGap={2}>
                  <HeartStroke size="sm" />
                  <TextV2 typographyVariant="label3" fontSize={pxToRem(13)} fontWeight={700} fontFamily="Inter UI">
                    Wedding Venue
                  </TextV2>
                </Flex>
              }
            />
            <Box id={MAP_CARDS_PORTAL_ID} position={'fixed'} bottom={{ _: 24, sm: 32 }} marginX={{ _: 16 }} width={{ _: '100%' }} maxWidth={{ _: 335, sm2: 398 }} />
            {reservedRoomBlocks.map(hotel => {
              const isHighlighted = activeMarker === hotel.id;
              return (
                <MapPinWithCard
                  usePortal={isMobile}
                  portalContainer={document.getElementById(MAP_CARDS_PORTAL_ID) as HTMLElement}
                  cardStyleOverride={{
                    bottom: 72,
                    width: '100%',
                    maxWidth: 259,
                    flexDirection: {
                      _: 'row',
                      sm2: 'column'
                    }
                  }}
                  key={hotel.id}
                  position={{ lat: hotel.latitude || 0, lng: hotel.longitude || 0 }}
                  isActive={isHighlighted}
                  showOnTop={isHighlighted}
                  isCardVisible={activeMarker === hotel.id}
                  onClick={e => {
                    e.stopPropagation();
                    if (isFullScreen) {
                      setActiveMarker(prev => (prev === hotel.id ? null : hotel.id!));
                    } else {
                      onSelectHotel(hotel.id);
                    }
                  }}
                  onDoubleClick={e => {
                    e.stopPropagation();
                  }}
                  label={
                    <Flex justifyContent="center" alignItems="center" columnGap={2}>
                      <HotelProduct size="sm" />
                      <TextV2 typographyVariant="label3" fontSize={pxToRem(13)} fontWeight={600} fontFamily="Inter UI">
                        ${hotel.strikeoutPricePerNight}
                      </TextV2>
                    </Flex>
                  }
                >
                  <Box
                    width={{
                      _: 104,
                      sm2: '100%'
                    }}
                    height={{
                      _: 142,
                      sm2: 167
                    }}
                    cursor={'pointer'}
                    flexShrink={0}
                    objectFit={'contain'}
                    backgroundSize={'cover'}
                    backgroundPosition={'center'}
                    backgroundRepeat={'no-repeat'}
                    backgroundImage={`url(${hotel?.photo?.url || 'https://withjoy.com/assets/public/hotelselection/empty-hotel-item.png'})`}
                    _hover={{ opacity: 0.8 }}
                    transition={animationTransition('opacity')}
                  />
                  <Flex
                    flexDirection={'column'}
                    flexGrow={1}
                    paddingX={5}
                    paddingY={{
                      _: 4,
                      sm2: 5
                    }}
                  >
                    <TextV2 typographyVariant="hed2">{hotel.displayName}</TextV2>
                    <Flex
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      paddingBottom={3}
                      paddingTop={{
                        _: '2px',
                        sm2: 2
                      }}
                    >
                      <TextV2
                        typographyVariant={{
                          _: 'label2',
                          sm2: 'body1'
                        }}
                      >
                        {distanceInMiles(latlng.lat, latlng.lng, hotel.latitude ?? 0, hotel.longitude ?? 0).toFixed(1)} miles away
                      </TextV2>
                      <HotelRating rating={4.5} ratingCount={0} size={isMobile ? 'sm' : 'md'} />
                    </Flex>
                  </Flex>
                </MapPinWithCard>
              );
            })}
          </JoyInteractableMap>
        </Flex>
      </Flex>
      <Divider display={{ _: 'flex', sm2: 'none' }} paddingTop={{ _: 8, sm2: 0 }} />
    </>
  );
};

HotelMap.displayName = 'HotelMap';

export { HotelMap };
